import { useState } from 'react';
import { LogOut, Shield, KeyRound, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../../api/client';

export default function Navbar() {
  const navigate = useNavigate();
  const stored = localStorage.getItem('user');
  const user = stored ? JSON.parse(stored) : null;
  const [showPwModal, setShowPwModal] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeClient');
    window.location.href = '/login';
  }

  function closePwModal() {
    setShowPwModal(false);
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setPwError('');
    setPwSuccess(false);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError('');
    if (newPw !== confirmPw) {
      setPwError('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      await changePassword(currentPw, newPw);
      setPwSuccess(true);
      setTimeout(closePwModal, 2000);
    } catch (err) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  }

  return (
    <nav className="bg-navy h-12 flex items-center justify-between pl-4 pr-6 shadow-lg flex-shrink-0 relative
      border-b-[3px] border-gold">
      <button
        onClick={() => {
          if (!user) return;
          if (user.role === 'CLIENT' && user.clientId) {
            navigate('/dashboard');
          } else {
            navigate('/clients');
          }
        }}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <img src="/bcd-icon.png" alt="BCD" className="h-6 w-auto" />
        <span className="text-white font-heading font-bold text-base uppercase tracking-wide">
          The Clarity Map
        </span>
      </button>

      {user && (
        <div className="flex items-center gap-3">
          {user.role === 'ADMIN' && (
            <button
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-1 text-xs"
              title="Admin"
            >
              <Shield className="w-3.5 h-3.5" />
              <span>Admin</span>
            </button>
          )}
          <button onClick={() => navigate('/account')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" title="Account settings">
            <span className="text-gray-300 text-sm">{user.name}</span>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover ring-2 ring-gold/30"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold text-sm font-semibold">
                {user.name?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
          </button>
          <button
            onClick={() => setShowPwModal(true)}
            className="text-gray-400 hover:text-white transition-colors"
            title="Change password"
          >
            <KeyRound className="w-4 h-4" />
          </button>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white transition-colors ml-1"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      )}

      {showPwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closePwModal} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <button onClick={closePwModal} className="absolute top-3 right-3 text-gray-400 hover:text-black">
              <X className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-heading font-bold mb-4">Change Password</h2>
            {pwSuccess ? (
              <p className="text-green-600 font-semibold text-center py-4">Password updated.</p>
            ) : (
              <form onSubmit={handleChangePassword} className="flex flex-col gap-3">
                <input type="password" placeholder="Current password" value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)} required
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                <input type="password" placeholder="New password (12+ characters)" value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} required
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                <input type="password" placeholder="Confirm new password" value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)} required
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                {pwError && <p className="text-red-500 text-xs">{pwError}</p>}
                <p className="text-xs text-gray-500">12+ characters. Must include uppercase, lowercase, number, and special character.</p>
                <button type="submit" disabled={pwLoading}
                  className="btn-gold text-sm disabled:opacity-50 mt-1">
                  {pwLoading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
